import React from 'react';
// import { logger } from "../../utils/logger";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Close as CloseIcon,
  Home as HomeIcon,
} from '@mui/icons-material';

interface NotFoundModalProps {
  isOpen: boolean;
  attemptedUrl: string;
  onClose: () => void;
}

/**
 * NotFoundModal - Displays a 404 error message with the attempted URL
 * Shows when users try to access old platform URLs or non-existent pages
 */
const NotFoundModal: React.FC<NotFoundModalProps> = ({
  isOpen,
  attemptedUrl,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleGoHome = () => {
    onClose();
    // The parent component will handle URL cleanup
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon color="error" sx={{ fontSize: 28 }} />
            <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
              Page Not Found
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: 'text.secondary' }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" color="text.primary" gutterBottom>
            The page you're looking for doesn't exist or has been moved.
          </Typography>

          {attemptedUrl && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Attempted URL:
              </Typography>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  fontSize: '0.875rem',
                }}
              >
                {attemptedUrl}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This might be an old link or a typo in the URL. You can return to our home page to find
            what you're looking for.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button
          onClick={handleGoHome}
          variant="contained"
          color="primary"
          startIcon={<HomeIcon />}
          autoFocus
        >
          Go to Home
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotFoundModal;









